import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import _isFunction from 'lodash/isFunction'
import Button, { TYPES } from '../Button/index'
import ArrowUpIcon from '../Icon/icons/ArrowUp/index'
import ArrowDownIcon from '../Icon/icons/ArrowDown/index'
import {
  getClientOffset,
  NULL_CLIENT_OFFSET,
} from '../Row.v3/utils'
import {
  getContainerHeight,
  calculateAnchors,
} from './utils'

const DRAG_MOMENTUM_SPEED = 120
const DRAG_MOMENTUM_DECAY = 0.3
const TRANSITION_DURATION = '500ms'

/**
 * Vertical Videos Slider
 * @param {Object} props
 * @param {Function} props.onSlideChange
 * @param {Function} props.onActiveIndexChange
 * @param {boolean} props.touchable
 * @param {Array<React.ReactNode>} props.children
 */
const VerticalVideosSlider = (props) => {
  const {
    onSlideChange,
    onActiveIndexChange,
    touchable,
    children,
  } = props
  const slides = React.Children.toArray(children) || []
  const slidesCount = slides.length
  const isDraggable = touchable
  const hasDragMomentum = touchable

  const [slideAnchors, setSlideAnchors] = useState([])
  const [index, setIndexState] = useState({ top: 0, bottom: 0 })
  const [maxIndex, setMaxIndex] = useState(slidesCount - 1)
  const [isDragging, setIsDragging] = useState(false)

  const getTranslateOffset = useCallback(
    (newIndex, newSlideAnchors = slideAnchors) => {
      const startIndex = newSlideAnchors[newIndex]
      const start = startIndex ? startIndex.start : null

      return start ? -1 * start : 0
    },
    [slideAnchors],
  )

  const translateOffset = useRef(() => getTranslateOffset(index.top))
  const momentumDebounceId = useRef()
  const transitionDebounceId = useRef()
  const areArrowsLocked = useRef(false)
  const currentDragSpeed = useRef(0)
  const resizeObserverRef = useRef()
  const containerRef = useRef(null)
  const slideContainerRef = useRef(null)
  const touchStartRef = useRef(NULL_CLIENT_OFFSET)
  const touchEndRef = useRef(NULL_CLIENT_OFFSET)
  const isMomentum = useRef(false)

  const slidesRefsCache = useRef([])

  const slidesRefs = useMemo(() => {
    const prev = slidesRefsCache.current
    return Array(slidesCount)
      .fill(null)
      .map((_, i) => prev[i] || React.createRef())
  }, [slidesCount])

  useLayoutEffect(() => {
    slidesRefsCache.current = slidesRefs
  }, [slidesRefs])

  const minScrollY = useMemo(() => {
    const startIndex = slideAnchors[maxIndex]
    const start = startIndex ? startIndex.start : null

    return start ? -1 * start : 0
  }, [slideAnchors, maxIndex])

  const getBoundIndex = useCallback(
    (newIndex, newMaxIndex = maxIndex) => Math.max(0, Math.min(newMaxIndex, newIndex)),
    [maxIndex],
  )

  const getScrollIndex = useCallback(
    (newTranslateOffset, newSlideAnchors = slideAnchors) => {
      const currentOffset = -1 * newTranslateOffset

      const newIndex = newSlideAnchors.reduce(
        (acc, { start, end, height }, i) => {
          if (currentOffset >= start) {
            acc.top = currentOffset >= start + height / 3 ? i + 1 : i
          }
          if (containerRef.current) {
            const containerHeight = getContainerHeight(containerRef.current)
            acc.bottom = currentOffset + containerHeight >= end
              ? i
              : acc.bottom
          } else {
            acc.bottom = slidesCount - 1
          }

          return acc
        },
        {
          top: 0,
          bottom: 0,
        },
      )

      return {
        top: getBoundIndex(newIndex.top),
        bottom: Math.max(Math.min(slidesCount - 1, newIndex.bottom), newIndex.top),
      }
    },
    [slidesCount, slideAnchors, getBoundIndex],
  )

  const setTranslateOffset = useCallback(
    ({ offset, idx }) => {
      requestAnimationFrame(() => {
        if (!slideContainerRef.current) {
          return
        }

        const newIndex = !idx ? getScrollIndex(offset) : idx

        if (transitionDebounceId.current) {
          cancelAnimationFrame(transitionDebounceId.current)
        }

        if (isDragging) {
          slideContainerRef.current.style.transitionDuration = '0ms'
        }

        slideContainerRef.current.style.transform = `translateY(${offset}px)`

        transitionDebounceId.current = requestAnimationFrame(() => {
          if (!slideContainerRef.current) {
            return
          }

          slideContainerRef.current.style.transitionDuration = TRANSITION_DURATION
        })

        translateOffset.current = offset
        setIndexState(newIndex)
      })
    },
    [
      isDragging,
      slideAnchors,
      slideAnchors.length,
      getScrollIndex,
      setIndexState,
    ],
  )

  const onResize = () => {
    const newSlideAnchors = calculateAnchors(slidesRefs)
    if (newSlideAnchors.length) {
      const containerHeight = getContainerHeight(slideContainerRef.current)
      const lastEnd = newSlideAnchors[newSlideAnchors.length - 1].end
      const newMaxIndex = getBoundIndex(
        newSlideAnchors.findIndex(({ start }) => start + containerHeight >= lastEnd),
        newSlideAnchors.length - 1,
      )
      const newTopIndex = getBoundIndex(index.top, newMaxIndex)
      const newTranslateOffset = getTranslateOffset(newTopIndex, newSlideAnchors)
      const newScrollIndex = getScrollIndex(newTranslateOffset, newSlideAnchors)
      setIndexState(newScrollIndex)
      setSlideAnchors(newSlideAnchors)
      setMaxIndex(newMaxIndex)
      setTranslateOffset({ offset: newTranslateOffset, idx: newScrollIndex })
    }
  }

  const onArrowClick = useCallback(
    (indexOffset, callback) => {
      if (!areArrowsLocked.current) {
        areArrowsLocked.current = true

        const boundIndexOffset = Math.min(1, indexOffset)
        const newBoundIndex = getBoundIndex(index.top + boundIndexOffset)

        requestAnimationFrame(() => {
          if (!slideContainerRef.current) {
            return
          }

          slideContainerRef.current.style.transitionDuration = TRANSITION_DURATION

          if (newBoundIndex !== index.top) {
            const newTranslateOffset = getTranslateOffset(newBoundIndex)
            const newScrollIndex = getScrollIndex(newTranslateOffset)

            slideContainerRef.current.addEventListener(
              'transitionend',
              () => {
                if (slidesRefs[newScrollIndex.top]
                  && slidesRefs[newScrollIndex.top].current
                ) {
                  slidesRefs[newScrollIndex.top].current.firstChild.focus()
                }
                areArrowsLocked.current = false
              },
              { once: true },
            )

            requestAnimationFrame(() => {
              slideContainerRef.current.style.transform = `translateY(${newTranslateOffset}px)`
            })

            translateOffset.current = newTranslateOffset

            setIndexState(newScrollIndex)

            if (callback) {
              callback(newScrollIndex)
            }
          } else {
            areArrowsLocked.current = false
          }
        })
      }
    },
    [
      slideAnchors,
      index,
      slides,
      getScrollIndex,
      setTranslateOffset,
      getBoundIndex,
      setIndexState,
    ],
  )

  const onTouchStart = useCallback(
    (e) => {
      if (momentumDebounceId.current) {
        cancelAnimationFrame(momentumDebounceId.current)
      }

      if (areArrowsLocked.current || !isDraggable || (e.touches && e.touches.length > 1)) {
        return
      }

      isMomentum.current = false
      setIsDragging(true)

      const offset = getClientOffset(e)
      touchStartRef.current = offset
      touchEndRef.current = offset
    },
    [isDraggable, setIsDragging],
  )

  const onTouchMove = useCallback(
    (e) => {
      e.stopPropagation()

      if (areArrowsLocked.current || isMomentum.current || !isDraggable || !isDragging) {
        return
      }

      touchEndRef.current = getClientOffset(e)
      const deltaX = touchStartRef.current.x - touchEndRef.current.x
      const deltaY = touchStartRef.current.y - touchEndRef.current.y
      touchStartRef.current = touchEndRef.current

      // To prevent horizontal scrolling when interacting with the row
      // only handle vertical drag if the vertical drag speed is greater
      // than the horizontal drag speed
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        currentDragSpeed.current = deltaY
        if (deltaY !== 0) {
          setTranslateOffset({ offset: translateOffset.current - deltaY })
        }
      }
    },
    [isDraggable, isDragging, setTranslateOffset],
  )

  const onTouchEnd = useCallback(
    (e) => {
      if (momentumDebounceId.current) {
        cancelAnimationFrame(momentumDebounceId.current)
      }

      if (areArrowsLocked.current || !isDraggable || (e.touches && e.touches.length > 0)) {
        return
      }

      if (hasDragMomentum) {
        isMomentum.current = true

        const momentumFunc = (speed) => {
          currentDragSpeed.current = speed
          momentumDebounceId.current = requestAnimationFrame(() => {
            const newTranslateOffset = translateOffset.current - speed

            if (Math.abs(speed) <= 1 || newTranslateOffset >= 0
            || newTranslateOffset <= minScrollY) {
              isMomentum.current = false
              setIsDragging(false)
              currentDragSpeed.current = 0
            } else {
              setTranslateOffset({ offset: newTranslateOffset })
              momentumFunc(speed * DRAG_MOMENTUM_DECAY)
            }
          })
        }

        momentumFunc(
          currentDragSpeed.current < 0
            ? Math.max(currentDragSpeed.current, -DRAG_MOMENTUM_SPEED)
            : Math.min(currentDragSpeed.current, DRAG_MOMENTUM_SPEED),
        )
      } else {
        setIsDragging(false)
      }
    },
    [
      hasDragMomentum,
      minScrollY,
      isDraggable,
      setIsDragging,
      setTranslateOffset,
    ],
  )

  const onSlideKeyDown = useCallback(
    (e) => {
      if (areArrowsLocked.current || (isDraggable && isDragging)) {
        e.preventDefault()
        e.stopPropagation()
      }
    },
    [isDraggable, isDragging],
  )

  useLayoutEffect(() => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect()
    }

    resizeObserverRef.current = new ResizeObserver(() => onResize())
    resizeObserverRef.current.observe(containerRef.current)
    slidesRefs.forEach(({ current }) => resizeObserverRef.current.observe(current))

    // Wait for the DOM to be updated
    requestAnimationFrame(() => {
      onResize()
    })
  }, [slidesCount])

  useEffect(() => {
    if (!areArrowsLocked.current && !(isDraggable && isDragging)) {
      if (momentumDebounceId.current) {
        cancelAnimationFrame(momentumDebounceId.current)
      }

      const newTranslateOffset = getTranslateOffset(index.top)

      setTranslateOffset({ offset: newTranslateOffset })

      if (translateOffset.current !== newTranslateOffset) {
        const slideRef = slidesRefs[index.top]
        if (slideRef && slideRef.current) {
          slideRef.current.firstChild.focus()
        }
      }

      touchStartRef.current = NULL_CLIENT_OFFSET
      touchEndRef.current = NULL_CLIENT_OFFSET
    }
  }, [isDragging, isDraggable])

  useEffect(() => {
    const onTransitionEnd = () => {
      onSlideChange(index.top)
    }

    if (slideContainerRef.current) {
      slideContainerRef.current.addEventListener(
        'transitionend',
        onTransitionEnd,
        { once: true },
      )
    }

    return () => {
      if (slideContainerRef.current) {
        slideContainerRef.current.removeEventListener('transitionend', onTransitionEnd)
      }
    }
  }, [index.top])

  useEffect(() => {
    if (_isFunction(onActiveIndexChange)) {
      onActiveIndexChange(index.top)
    }
  }, [index.top, onActiveIndexChange])

  return (
    <div
      className="vertical-videos-page__slider-wrapper"
    >
      <div
        className="vertical-videos-page__slider-items-and-controls-container"
        ref={containerRef}
        onMouseLeave={onTouchEnd}
      >
        <div className="vertical-videos-page__slider-items-list-container">
          <ul
            ref={slideContainerRef}
            className="vertical-videos-page__slider-items-list"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchEnd}
            onMouseDown={onTouchStart}
            onMouseMove={onTouchMove}
            onMouseUp={onTouchEnd}
          >
            {slides.map((slide, i) => {
              return (
                <li
                  className="vertical-videos-page__slider-item"
                  ref={slidesRefs[i]}
                  key={i}
                  onKeyDown={onSlideKeyDown}
                >
                  <div className="vertical-videos-page__slider-item-inner">
                    {React.cloneElement(slide)}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
        {!touchable && (
          <div className="vertical-videos-page__slider-controls-container">
            <Button
              type={TYPES.ICON}
              className="vertical-videos-page__slider-control"
              onClick={() => onArrowClick(-1)}
              iconSvg={<ArrowUpIcon size="50px" />}
              disabled={index.top === 0}
            />
            <Button
              type={TYPES.ICON}
              className="vertical-videos-page__slider-control"
              onClick={() => onArrowClick(1)}
              iconSvg={<ArrowDownIcon size="50px" />}
              disabled={index.top === maxIndex}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default compose(
  connectRedux(
    (state) => ({
      touchable: state.app.getIn(['viewport', 'touchable']),
    }),
  ),
)(VerticalVideosSlider)
